import { jwtDecode } from "jwt-decode";
import { getItem, setItem, removeItem } from "./storage";

export const getToken = () => {
  const adminToken = getItem("adminToken");
  if (adminToken) return { token: adminToken, type: "admin" };
  
  const workerToken = getItem("workerToken");
  if (workerToken) return { token: workerToken, type: "worker" };
  
  const customerToken = getItem("customerToken");
  if (customerToken) return { token: customerToken, type: "customer" };
  
  const donorToken = getItem("donorToken");
  if (donorToken) return { token: donorToken, type: "donor" };
  
  return null;
};

export const isAuthenticated = () => {
  const tokenData = getToken();
  if (!tokenData) return false;
  
  try {
    const decodedToken = jwtDecode(tokenData.token);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp > currentTime;
  } catch (error) {
    console.error("Token validation error:", error);
    return false;
  }
};

export const getUserRole = () => {
  const tokenData = getToken();
  if (!tokenData) return null;
  
  try {
    const decodedToken = jwtDecode(tokenData.token);
    return decodedToken.Role;
  } catch (error) {
    console.error("Token validation error:", error);
    return null;
  }
};

export const getUserData = () => {
  const tokenData = getToken();
  if (!tokenData) return null;
  
  try {
    const decodedToken = jwtDecode(tokenData.token);
    return decodedToken;
  } catch (error) {
    console.error("Token validation error:", error);
    return null;
  }
};

export const cleanupTokenData = (tokenKey) => {
  // Check if impersonating - if so, don't clear storage, restore super admin instead
  const isImpersonated = getItem("isImpersonated") === "true";
  const oldSession = getItem("oldSession");
  const oldAdminId = getItem("oldAdminId");
  
  if (isImpersonated && oldSession && tokenKey === "adminToken") {
    // When impersonating and adminToken is being cleaned up, restore super admin session
    setItem("adminToken", oldSession);
    if (oldAdminId) {
      setItem("admin_id", oldAdminId);
    }
    removeItem("oldSession");
    removeItem("oldAdminId");
    removeItem("isImpersonated");
    removeItem("impersonatedBy");
    // Don't clear adminToken or other company data - let the restore handle it
    return;
  }
  
  removeItem(tokenKey);
  
  if (tokenKey === "adminToken") {
    removeItem("admin_id");
    removeItem("CompanyId");
  } else if (tokenKey === "workerToken") {
    removeItem("worker_id");
  } else if (tokenKey === "customerToken") {
    removeItem("CustomerId");
  } else if (tokenKey === "donorToken") {
    removeItem("DonorId");
  }
};

export const logout = (navigate, isSuperLogOut = false) => {
  // Check if impersonating - if so, return to super admin instead of logging out
  const isImpersonated = getItem("isImpersonated") === "true";
  const oldSession = getItem("oldSession");
  const oldAdminId = getItem("oldAdminId");
  const appUrl = getItem("appUrl"); // Get APP_URL from env (stored during sign-in)

  if (isImpersonated && oldSession && isSuperLogOut) {
    // Clear company-related data first
    removeItem("CompanyId");
    removeItem("CompanyUrl");
    removeItem("isImpersonated");
    removeItem("impersonatedBy");
    removeItem("CustomerId");
    removeItem("currentLogoPath");
    removeItem("oldSession");
    removeItem("oldAdminId");
    removeItem("appUrl");
    localStorage.removeItem("companyName");

    const navigationPath = "/superadmin/company";

    // Same-origin restore: write the super-admin token directly and SPA-nav.
    const sameOrigin =
      !appUrl ||
      (typeof window !== "undefined" &&
        appUrl.startsWith(window.location.origin));

    if (sameOrigin) {
      setItem("adminToken", oldSession);
      if (oldAdminId) {
        setItem("admin_id", oldAdminId);
      }
      if (navigate) {
        navigate(navigationPath, { replace: true });
      } else if (typeof window !== "undefined") {
        window.history.pushState({}, "", navigationPath);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
      return;
    }

    // Cross-origin: full nav is unavoidable (storage doesn't follow origins),
    // so encode session into the hash for restoreAuthFromHash() at the target.
    const superAdminData = {
      adminToken: oldSession,
      adminId: oldAdminId || null,
      appUrl,
    };
    const encodedData = btoa(JSON.stringify(superAdminData));
    window.location.href = `${appUrl}${navigationPath}#auth=${encodedData}`;
    return;
  }

  // Normal logout - clear everything
  removeItem("adminToken");
  removeItem("admin_id");
  removeItem("CompanyId");
  removeItem("workerToken");
  removeItem("worker_id");
  removeItem("customerToken");
  removeItem("CustomerId");
  removeItem("donorToken");
  removeItem("DonorId");
  removeItem("oldSession");
  removeItem("isImpersonated");
  removeItem("impersonatedBy");
  removeItem("currentLogoPath");
  sessionStorage.removeItem("customerDetail");
  // Remove company name from localStorage on logout
  localStorage.removeItem("companyName");

  if (navigate) {
    navigate("/auth/login", { replace: true });
  } else if (typeof window !== "undefined") {
    // No router navigator passed in — push history state instead of doing a
    // full reload so React Router still picks up the change and shows the
    // existing skeleton/transition state.
    window.history.pushState({}, "", "/auth/login");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
};

export const hasRole = (requiredRole) => {
  const role = getUserRole();
  if (!role) return false;
  
  if (requiredRole === "SuperAdmin") {
    const userData = getUserData();
    return role === "Admin" && userData?.AdminId;
  }
  
  return role === requiredRole;
};

export const isTokenExpired = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  } catch (error) {
    return true;
  }
};

export default {
  getToken,
  isAuthenticated,
  getUserRole,
  getUserData,
  cleanupTokenData,
  logout,
  hasRole,
  isTokenExpired
}; 